// S59/S60 (2026-06-06) — Mobile-canvas opt-in flag.
//
// The Strategy Builder is desktop-first; on phones the canvas is gated
// behind a polished "desktop-only" notice. This hook is the single
// source of truth for the per-browser escape hatch the gate offers
// ("Open canvas anyway") so the rest of the app can read it without
// every consumer reimplementing the localStorage dance.
//
// Stored per-browser on purpose: it's a local UX preference, not a
// user-record setting. Clearing browser data resets to the default
// gated behaviour. Storage failures (private mode, quota) degrade
// silently to `false` — the user just sees the gate, no error UI.
//
// `storage` event listener picks up changes from other tabs in the
// same browser (rare but cheap to support).

import { useEffect, useState } from 'react';

export const BUILDER_MOBILE_OVERRIDE_KEY = 'tradeatory.builderMobileOverride';

export function readBuilderMobileOverride(): boolean {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage.getItem(BUILDER_MOBILE_OVERRIDE_KEY) === '1'; }
  catch { return false; }
}

export function writeBuilderMobileOverride(on: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (on) window.localStorage.setItem(BUILDER_MOBILE_OVERRIDE_KEY, '1');
    else    window.localStorage.removeItem(BUILDER_MOBILE_OVERRIDE_KEY);
  } catch { /* private mode / storage disabled — silent */ }
  // Hand-fire a synthetic storage event so listeners in the SAME
  // tab pick up the change. The browser only dispatches `storage`
  // to OTHER tabs by default — so without this, components in the
  // same tab would miss the update until a remount.
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    try {
      window.dispatchEvent(new StorageEvent('storage', { key: BUILDER_MOBILE_OVERRIDE_KEY }));
    } catch { /* IE-style StorageEvent ctor failure — degrade quietly */ }
  }
}

/**
 * Subscribe to the override flag. Returns a `[value, setValue]` tuple
 * so callers can both read AND flip the flag in one import.
 *
 * The setter writes localStorage AND updates local React state so the
 * component re-renders without waiting for the storage event round-trip.
 */
export function useBuilderMobileOverride(): [boolean, (next: boolean) => void] {
  const [on, setOn] = useState<boolean>(() => readBuilderMobileOverride());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: StorageEvent) => {
      if (e.key && e.key !== BUILDER_MOBILE_OVERRIDE_KEY) return;
      setOn(readBuilderMobileOverride());
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const set = (next: boolean) => {
    writeBuilderMobileOverride(next);
    setOn(next);
  };

  return [on, set];
}
